import React from 'react';
import { Plus, Trash2, Image } from 'lucide-react';
import ColorEditor from '../ColorEditor';
import { resolveImageUrl } from '../../utils/imageUtils';

const TeamEditor = ({ content, onContentChange, onMultipleContentChanges, uploading, onImageUpload, onOpenImagePicker, site, onCreateCustomTheme }) => {
  const handleMemberChange = (index, field, value) => {
    const updatedMembers = [...(content.members || [])];
    updatedMembers[index] = { ...updatedMembers[index], [field]: value };
    onContentChange('members', updatedMembers);
  };

  const handleSpecialtyChange = (memberIndex, specialtyIndex, value) => {
    const updatedMembers = [...(content.members || [])];
    const updatedSpecialties = [...(updatedMembers[memberIndex].specialties || [])];
    updatedSpecialties[specialtyIndex] = value;
    updatedMembers[memberIndex] = { ...updatedMembers[memberIndex], specialties: updatedSpecialties };
    onContentChange('members', updatedMembers);
  };

  const addMember = () => {
    const newMember = {
      name: '',
      role: '',
      description: '',
      specialties: [''],
      imageUrl: '/uploads/placeholder-person.jpg'
    };
    onContentChange('members', [...(content.members || []), newMember]);
  };

  const removeMember = (index) => {
    const updatedMembers = (content.members || []).filter((_, i) => i !== index);
    onContentChange('members', updatedMembers);
  };

  const addSpecialty = (memberIndex) => {
    const updatedMembers = [...(content.members || [])];
    updatedMembers[memberIndex].specialties = [...(updatedMembers[memberIndex].specialties || []), ''];
    onContentChange('members', updatedMembers);
  };

  const removeSpecialty = (memberIndex, specialtyIndex) => {
    const updatedMembers = [...(content.members || [])];
    updatedMembers[memberIndex].specialties = updatedMembers[memberIndex].specialties.filter((_, i) => i !== specialtyIndex);
    onContentChange('members', updatedMembers);
  };

  return (
    <div className="space-y-4">
      {/* Section Title */}
      <div>
        <label className="block text-xs font-medium text-gray-900 mb-1">Section title</label>
        <input
          type="text"
          value={content.title || ''}
          onChange={(e) => onContentChange('title', e.target.value)}
          className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
          placeholder="Meet Our Team"
        />
      </div>

      {/* Section Subtitle */}
      <div>
        <label className="block text-xs font-medium text-gray-900 mb-1">Section subtitle</label>
        <textarea
          rows={2}
          value={content.subtitle || ''}
          onChange={(e) => onContentChange('subtitle', e.target.value)}
          className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent resize-none"
          placeholder="The passionate professionals behind our success"
        />
      </div>

      {/* Layout Options */}
      <div>
        <label className="block text-xs font-medium text-gray-900 mb-1">Layout</label>
        <div className="space-y-2">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Grid Columns</label>
            <select
              value={content.layout?.columns || 'auto'}
              onChange={(e) => onContentChange('layout', { ...(content.layout || {}), columns: e.target.value })}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="auto">Auto (responsive)</option>
              <option value="1">1 Column</option>
              <option value="2">2 Columns</option>
              <option value="3">3 Columns</option>
              <option value="4">4 Columns</option>
            </select>
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Card Style</label>
            <select
              value={content.layout?.cardStyle || 'simple'}
              onChange={(e) => onContentChange('layout', { ...(content.layout || {}), cardStyle: e.target.value })}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="simple">Simple</option>
              <option value="card">Card with background</option>
              <option value="bordered">Card with border</option>
              <option value="shadow">Card with shadow</option>
            </select>
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Image Style</label>
            <select
              value={content.layout?.imageStyle || 'circle'}
              onChange={(e) => onContentChange('layout', { ...(content.layout || {}), imageStyle: e.target.value })}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="circle">Circle</option>
              <option value="square">Square</option>
              <option value="rounded">Rounded Square</option>
            </select>
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Image Size</label>
            <select
              value={content.layout?.imageSize || 'medium'}
              onChange={(e) => onContentChange('layout', { ...(content.layout || {}), imageSize: e.target.value })}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="small">Small (96px)</option>
              <option value="medium">Medium (128px)</option>
              <option value="large">Large (160px)</option>
            </select>
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Text Alignment</label>
            <select
              value={content.layout?.textAlign || 'center'}
              onChange={(e) => onContentChange('layout', { ...(content.layout || {}), textAlign: e.target.value })}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
          </div>
        </div>
      </div>

      {/* Team Members */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-xs font-medium text-gray-900">Team members</label>
          <button
            onClick={addMember}
            className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
          >
            <Plus className="w-3 h-3" />
            Add member
          </button>
        </div>

        <div className="space-y-3">
          {(content.members || []).map((member, memberIndex) => (
            <div key={memberIndex} className="border border-gray-200 rounded p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-700">Member {memberIndex + 1}</span>
                <button
                  onClick={() => removeMember(memberIndex)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>

              {/* Member Image */}
              <div>
                <label className="block text-xs font-medium text-gray-900 mb-1">Photo</label>
                <div className="flex gap-2">
                  <div 
                    className="flex-1 h-20 bg-gray-100 border-2 border-dashed border-gray-300 rounded flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors relative overflow-hidden"
                    onClick={() => document.getElementById(`teamMemberImageInput-${memberIndex}`).click()}
                  >
                    {member.imageUrl ? (
                      <img 
                        src={resolveImageUrl(member.imageUrl)} 
                        alt={member.name || `Team member ${memberIndex + 1}`}
                        className="w-full h-full object-cover rounded"
                      />
                    ) : (
                      <div className="text-center">
                        <Image className="w-4 h-4 text-gray-400 mx-auto mb-1" />
                        <div className="text-xs text-gray-500">
                          {uploading ? 'Uploading...' : 'Upload'}
                        </div>
                      </div>
                    )}
                    <input
                      id={`teamMemberImageInput-${memberIndex}`}
                      type="file"
                      accept="image/*"
                      onChange={(e) => onImageUpload(e.target.files[0], `members.${memberIndex}.imageUrl`)}
                      className="hidden"
                      disabled={uploading}
                    />
                  </div>
                  {onOpenImagePicker && (
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => onOpenImagePicker(`members.${memberIndex}.imageUrl`, 'professional team member portrait', 'unsplash')}
                        className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition min-w-0 flex flex-col items-center justify-center"
                        disabled={uploading}
                      >
                        <Image className="w-3 h-3 mb-1" />
                        <span>Unsplash</span>
                      </button>
                      <button
                        onClick={() => onOpenImagePicker(`members.${memberIndex}.imageUrl`, 'professional business portrait', 'pexels')}
                        className="px-3 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 transition min-w-0 flex flex-col items-center justify-center"
                        disabled={uploading}
                      >
                        <Image className="w-3 h-3 mb-1" />
                        <span>Pexels</span>
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Photo Credit for Team Member Image */}
                {member.imageUrlCredit && (
                  <div className="text-xs text-gray-500 mt-1">
                    Photo by{' '}
                    <a 
                      href={member.imageUrlCredit.photographerUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      {member.imageUrlCredit.photographer}
                    </a>
                    {' '}on Unsplash
                  </div>
                )}
              </div>

              {/* Member Name */}
              <div>
                <label className="block text-xs font-medium text-gray-900 mb-1">Name</label>
                <input
                  type="text"
                  value={member.name || ''}
                  onChange={(e) => handleMemberChange(memberIndex, 'name', e.target.value)}
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                  placeholder="John Smith"
                />
              </div>

              {/* Member Role */}
              <div>
                <label className="block text-xs font-medium text-gray-900 mb-1">Role</label>
                <input
                  type="text"
                  value={member.role || ''}
                  onChange={(e) => handleMemberChange(memberIndex, 'role', e.target.value)}
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Senior Developer"
                />
              </div>

              {/* Member Description */}
              <div>
                <label className="block text-xs font-medium text-gray-900 mb-1">Description</label>
                <textarea
                  rows={2}
                  value={member.description || ''}
                  onChange={(e) => handleMemberChange(memberIndex, 'description', e.target.value)}
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Brief description of expertise and background"
                  maxLength={150}
                />
              </div>

              {/* Member Specialties */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-medium text-gray-900">Specialties</label>
                  <button
                    onClick={() => addSpecialty(memberIndex)}
                    className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    <Plus className="w-3 h-3" />
                    Add
                  </button>
                </div>
                <div className="space-y-1">
                  {(member.specialties || []).map((specialty, specialtyIndex) => (
                    <div key={specialtyIndex} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={specialty}
                        onChange={(e) => handleSpecialtyChange(memberIndex, specialtyIndex, e.target.value)}
                        className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Area of expertise"
                      />
                      <button
                        onClick={() => removeSpecialty(memberIndex, specialtyIndex)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {(content.members || []).length === 0 && (
          <div className="text-center py-4 border border-dashed border-gray-300 rounded">
            <p className="text-xs text-gray-500 mb-2">No team members added yet</p>
            <button
              onClick={addMember}
              className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1 mx-auto"
            >
              <Plus className="w-3 h-3" />
              Add your first team member
            </button>
          </div>
        )}
      </div>

      {/* Color Theme - Always at bottom */}
      <ColorEditor
        value={content.colorTheme || 'default'}
        onChange={(value) => onContentChange('colorTheme', value === 'default' ? null : value)}
        label="Section Color Theme"
        showReset={true}
        resetValue="default"
        siteTheme={site?.design?.colorTheme ? {
          name: site.design.colorThemeName || 'Site Theme',
          colors: site.design.colorTheme
        } : null}
        customThemes={site?.design?.customThemes || []}
        onCreateCustomTheme={onCreateCustomTheme}
      />
    </div>
  );
};

export default TeamEditor;